import React, { Fragment, useContext, useEffect, useState } from 'react';
import { LayoutContext } from '../../contexts/layout.context';
import { useLocation, useNavigate } from 'react-router-dom';
import Head from '../../components/Head/Head';
import HomeworkCard from '../../components/Homework/HomeworkCard';
import HeadTooltip from '../../components/Tooltip/HeadTooltip';
import HomeworkTooltipHint from '../../components/Tooltip/HomeworkTooltipHint';
import { useDispatch, useSelector } from 'react-redux';
import { homeworkSelectors } from '../../store/selectors/homework';
import { authSelectors } from '../../store/selectors/auth';
import { getToken } from '../../utils/token';
import { getHomeworkThunk } from '../../store/thunks/homework';
import { homeworkColors } from '../../components/Homework/constants';
import { getUserInfoThunk } from '../../store/thunks/profile';
import { findFirstOpenHomeworkBlock } from '../../utils/homeworkNext';
import usePreviewFeature from '../../hooks/usePreviewFeature';
import Spinner from '../../common/Spinner/Spinner';
import Plug from '../../components/plug/plug.component';

const HOMEWORK_DONE = 4;
const PATH_LOCKED_TEXT = 'Відкриється, коли пройдете попереднє завдання';

const isHomeworkDone = (item) => Number(item.homework_icon_color) === HOMEWORK_DONE;

const tasksWord = (count) => {
  const n = Math.abs(count) % 100;
  const n1 = n % 10;
  if (n > 10 && n < 20) return 'вправ';
  if (n1 === 1) return 'вправи';

  return 'вправ';
};

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12.5 10 17l9-10" style={{ fill: 'none' }} />
  </svg>
);

const Homework = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setLayout } = useContext(LayoutContext);

  const savedAccordion = sessionStorage.getItem('openAccordion');
  const eventId = JSON.parse(localStorage.getItem('eventId'));
  const realHomeworkId = JSON.parse(localStorage.getItem('realHomeworkId'));
  const auth = useSelector(authSelectors);
  const token = auth.token;
  const homework = useSelector(homeworkSelectors);
  const homeworkItems = homework.items;
  const homeworkInfo = homework.info;
  const load = homework.load;
  const loadError = homework.error;

  const [openAccordion, setOpenAccordion] = useState(null);
  const [currentId, setCurrentId] = useState(null);
  const isRedesign = usePreviewFeature('courses-redesign');
  const showDirection = usePreviewFeature('homework-direction-flags');
  const isPath = usePreviewFeature('homework-path') && isRedesign;
  const language = showDirection ? homeworkInfo.language : null;

  getToken(token);

  useEffect(() => {
    document.title = 'Домашнє завдання | New Brain';
    setLayout({ page: 'Домашнє завдання' });
  }, [setLayout]);

  useEffect(() => {
    if (token) {
      dispatch(getUserInfoThunk({ token }));
    }
  }, [token]);

  useEffect(() => {
    if ((eventId || realHomeworkId) && token) {
      const data = { event_id: eventId, homework_id: realHomeworkId, token };
      dispatch(getHomeworkThunk(data));
    }
  }, [token, eventId, realHomeworkId]);

  useEffect(() => {
    if (load || homeworkItems.length === 0) return;

    if (!isRedesign) {
      setOpenAccordion(savedAccordion ? Number(savedAccordion) : homeworkItems[0]?.id);

      return;
    }

    const saved = homeworkItems.find((item) => item.id === Number(savedAccordion));
    const firstOpen = findFirstOpenHomeworkBlock(homeworkItems);
    const target = isPath
      ? firstOpen || homeworkItems[0]
      : saved && Number(saved.homework_icon_color) !== HOMEWORK_DONE
        ? saved
        : firstOpen || saved || homeworkItems[0];

    setOpenAccordion(target.id);
    setCurrentId(Number(target.homework_icon_color) !== HOMEWORK_DONE ? target.id : null);

    if (target.id !== homeworkItems[0].id) {
      requestAnimationFrame(() => {
        document.querySelector(`[data-homework-card="${target.id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }, [homeworkItems, load, isRedesign, isPath, isRedesign ? null : savedAccordion]);

  useEffect(() => {
    if (location.state?.from && !sessionStorage.getItem('initialRoute')) {
      sessionStorage.setItem('initialRoute', location.state.from);
    }
  }, [location.state]);


  const goBack = () => {
    const initialRoute = sessionStorage.getItem('initialRoute');

    if (initialRoute) {
      navigate(initialRoute);
    } else {
      navigate('/schedule');
    }

    sessionStorage.removeItem('initialRoute');
    sessionStorage.removeItem('openAccordion');
  };


  const toggleAccordion = (id) => {
    const newOpen = openAccordion === id ? null : id;
    setOpenAccordion(newOpen);
    if (newOpen === null) {
      // Раніше сюди писався рядок "null", який потім Number() перетворював
      // на NaN — і жодна картка не відкривалась.
      sessionStorage.removeItem('openAccordion');
    } else {
      sessionStorage.setItem('openAccordion', String(newOpen));
    }
  };


  const doneCount = homeworkItems.filter((item) => Number(item.homework_icon_color) === HOMEWORK_DONE).length;
  const totalCount = homeworkItems.length;
  const isAllDone = totalCount > 0 && doneCount === totalCount;
  const nextItem = homeworkItems.find((item) => item.id === currentId);
  const donePercent = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;
  const firstOpenIndex = homeworkItems.findIndex((item) => !isHomeworkDone(item));

  const getPathState = (item, idx) => {
    if (isHomeworkDone(item)) return { stage: 'done', locked: Boolean(item.is_locked) };
    if (item.id === currentId) return { stage: 'current', locked: Boolean(item.is_locked) };

    const isStarted = item.max_iteration > 0 || Boolean(item.is_show_continue_button);
    const isBlockedByOrder = firstOpenIndex >= 0 && idx > firstOpenIndex && !isStarted;

    if (item.is_locked || isBlockedByOrder) return { stage: 'todo', locked: true, byOrder: !item.is_locked };

    return { stage: 'open', locked: false };
  };

  const getStage = (item) => {
    if (!isRedesign) return '';
    if (isHomeworkDone(item)) return 'done';

    return item.id === currentId ? 'current' : 'todo';
  };

  return (
    <div className={`homework container ${isRedesign ? 'homework--v2' : ''}`.trim()}>
      <Head
        textHead={homeworkInfo.title ? homeworkInfo.title : ''}
        subtitle={homeworkInfo.program_name ? homeworkInfo.program_name : ''}
        subtitleSecond={homeworkInfo.lesson_name ? homeworkInfo.lesson_name : ''}
        goBack={goBack}
        tooltip={<HeadTooltip text={<HomeworkTooltipHint />} />}
        infoText={homeworkInfo.info_text ? homeworkInfo.info_text : ''}
      />
      {!load && homeworkItems.length === 0 && (
        <Plug
          plug={loadError || (!eventId && !realHomeworkId
            ? 'Не вдалося відкрити домашнє завдання. Поверніться до розкладу або курсу і натисніть «Вправи» ще раз.'
            : 'Домашнє завдання ще не додали. Загляньте трохи пізніше.')}
          icon="file-closed"
        />
      )}
      {load ? <Spinner /> : <>
        {isRedesign && totalCount > 0 && (
          <section className={`hw-summary ${isAllDone ? 'hw-summary--done' : ''}`.trim()}>
            {isAllDone && <span className="hw-summary__icon"><CheckIcon /></span>}
            <div className="hw-summary__main">
              <div className="hw-summary__title">
                {isAllDone ? 'Усе домашнє завдання виконано!' : `Виконано ${doneCount} з ${totalCount} ${tasksWord(totalCount)}`}
              </div>
              {isAllDone ? (
                <p className="hw-summary__text">Молодець! Можна повторити будь-яку вправу ще раз — результат збережеться.</p>
              ) : (
                nextItem && <p className="hw-summary__text">Наступна: <b>{nextItem.title}</b></p>
              )}
              <div className="hw-summary__bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={donePercent}>
                <span style={{ width: `${donePercent}%` }} />
              </div>
            </div>
            {isAllDone && (
              <button type="button" className="v2-btn v2-btn--green v2-btn--sm hw-summary__btn" onClick={goBack}>
                Повернутися
              </button>
            )}
          </section>
        )}
        <div className={`homework__cards ${isPath ? 'homework__cards--path' : ''}`.trim()}>
          {homeworkItems.map((homework, idx) => {
            const pathState = isPath ? getPathState(homework, idx) : null;

            return (
              <Fragment key={homework.id ?? idx}>
                <HomeworkCard
                  count={homework.is_review ? '!' : idx + 1}
                  id={homework.id}
                  variant={homework.is_review ? 'review' : ''}
                  locked={pathState ? pathState.locked : Boolean(homework.is_locked)}
                  lockedText={pathState?.byOrder ? PATH_LOCKED_TEXT : homework.locked_text || 'Доступне після проходження всіх завдань'}
                  title={homework.title}
                  color={homeworkColors[homework.homework_icon_color]}
                  stage={pathState ? pathState.stage : getStage(homework)}
                  path={isPath}
                  description={homework.description}
                  testsCount={homework.total_questions}
                  attempts={homework.max_iteration}
                  toggleAccordion={toggleAccordion}
                  isOpen={!isPath && openAccordion === homework.id}
                  isContinue={homework.max_iteration > 0}
                  load={load}
                  continueBtn={homework.is_show_continue_button}
                  homeworkElId={homeworkInfo.el_homework_id}
                  homeworkBlockId={homework.el_homework_block_id}
                  direction={language ? homework.words_direction : null}
                  language={language}
                  token={token}
                />
                {isPath && idx < homeworkItems.length - 1 && (
                  <div className={`hw-link ${isHomeworkDone(homework) ? 'hw-link--done' : ''}`.trim()} aria-hidden="true" />
                )}
              </Fragment>
            );
          })}
        </div>
      </>}
    </div>
  );
};

export default Homework;